import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatsAPI, agentsAPI } from '../../services/api';
import api from '../../services/api';
import { Conversation, CompanyUser, Agent, MessageAttachment } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import AgentMessageContent from '../shared/AgentMessageContent';
import { Download, Info, Send, User as UserIcon, X, Square, CheckSquare, Calendar, ExternalLink, Paperclip, Hand, UserCheck, Headphones } from 'lucide-react';
import { ConversationComment } from '../../types';
import { serverIsoToMillis, formatServerDate, formatServerTime, formatServerDateTime } from '../../utils/datetime';
import { useAuth } from '../../contexts/AuthContext';

interface ChatsTabProps {
    agents: Agent[];
    teamMembers: CompanyUser[];
    hasAssignPermission: boolean;
    mobilePanel: 'list' | 'chat' | 'details';
    selectedChat: Conversation | null;
    setSelectedChat: React.Dispatch<React.SetStateAction<Conversation | null>>;
    setMobilePanel: (panel: 'list' | 'chat' | 'details') => void;
    showDetails: boolean;
    setShowDetails: (show: boolean) => void;
    // Deep-link: auto-select a specific chat by session_id
    initialChatId?: string;
    onInitialChatHandled?: () => void;
}

const ChatsTab: React.FC<ChatsTabProps> = ({
    agents,
    teamMembers,
    hasAssignPermission,
    mobilePanel,
    selectedChat,
    setSelectedChat,
    setMobilePanel,
    showDetails,
    setShowDetails,
    initialChatId,
    onInitialChatHandled,
}) => {
    const { showToast } = useToast();
    const { theme } = useTheme();
    const { user: currentUser } = useAuth();
    const isDark = theme === 'dark';

    // Local State
    const [chats, setChats] = useState<Conversation[]>([]);
    const [loadingChats, setLoadingChats] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [replyText, setReplyText] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    const [pendingReplyAttachments, setPendingReplyAttachments] = useState<Array<{ file: File; previewUrl: string; type: 'image' | 'document' }>>([]);
    const replyFileInputRef = useRef<HTMLInputElement>(null);



    // Published Toggle State
    const currentAgent = selectedChat ? agents.find(a => a.id === selectedChat.agent_id) : null;
    const [publishedEnabled, setPublishedEnabled] = useState(false);
    const [updatingPublished, setUpdatingPublished] = useState(false);

    useEffect(() => {
        if (currentAgent) {
            setPublishedEnabled(!!currentAgent.is_published);
        }
    }, [currentAgent]);

    const handleTogglePublished = async () => {
        if (!currentAgent) return;
        setUpdatingPublished(true);
        const newValue = !publishedEnabled;
        setPublishedEnabled(newValue);

        try {
            await agentsAPI.update(currentAgent.id, {
                is_published: newValue
            });
            showToast(`Agent ${currentAgent.config.agent_name} is now ${newValue ? 'Live' : 'Draft'}`, 'success');
        } catch (error) {
            console.error('Failed to toggle published status', error);
            setPublishedEnabled(!newValue);
            showToast('Failed to update published status', 'error');
        } finally {
            setUpdatingPublished(false);
        }
    };

    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Derived State
    const filteredChats = chats.filter(c => {
        if (!dateFrom && !dateTo) return true;
        // A zero-message conversation has no `last_message_at`; fall back to
        // when it was created so it still lands in the right date bucket. If
        // nothing parses, EXCLUDE it while a filter is active — the previous
        // `parseServerIso(...).getTime()` yielded NaN, and every NaN compare
        // is false, so those rows slipped past both bounds and always showed.
        const ts = serverIsoToMillis(c.last_message_at)
            ?? serverIsoToMillis(c.created_at)
            ?? serverIsoToMillis(c.started_at);
        if (ts === null) return false;
        if (dateFrom && ts < new Date(dateFrom).getTime()) return false;
        if (dateTo && ts > new Date(dateTo + 'T23:59:59').getTime()) return false;
        return true;
    });

    const allFilteredSelected = filteredChats.length > 0 && filteredChats.every(c => selectedChatIds.has(c.session_id));
    const someFilteredSelected = filteredChats.some(c => selectedChatIds.has(c.session_id));

    useEffect(() => {
        fetchChats();
    }, [selectedAgentId]);

    useEffect(() => {
        if (selectedChat) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedChat?.messages]);

    // Plan-HumanAgentHandoff-2026-06-12 — staff live feed.
    // Storm shipped `POST /api/chats/ws-token` (Bearer-auth, mints a
    // 5-minute scoped JWT) + `GET /api/chats/ws/staff/{session_id}?token=...`
    // (back/api/chats.py:427 + chats.py:455). The WS receives every
    // message-write fan-out from `ConnectionManager.broadcast_to_session`
    // — both customer-typed messages (after the AI suppression gate
    // saves them to the transcript) AND staff replies. We append to
    // `selectedChat.messages` so the open chat panel updates in real
    // time without a refetch, and refresh `fetchChats()` so the
    // chat-list preview / unread badge stay in sync.
    //
    // Lifecycle: opens when a chat is selected, closes on chat
    // deselect / component unmount / staff-token expiry (5min — handled
    // by re-minting on the next selection if a stale close arrives).
    const staffWsRef = useRef<WebSocket | null>(null);
    useEffect(() => {
        const sid = selectedChat?.session_id;
        if (!sid) return;
        let cancelled = false;
        let ws: WebSocket | null = null;
        (async () => {
            try {
                const { data: tokenResp } = await chatsAPI.issueWsToken();
                if (cancelled) return;
                const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8000';
                const wsBase = apiUrl.replace(/^http/, 'ws');
                ws = new WebSocket(`${wsBase}/api/chats/ws/staff/${encodeURIComponent(sid)}?token=${encodeURIComponent(tokenResp.token)}`);
                staffWsRef.current = ws;
                ws.onmessage = (e) => {
                    let data: any;
                    try { data = JSON.parse(e.data); } catch { return; }
                    // Storm task #31 fan-out shape — `{type:"new_message", message:{role, content, timestamp, from_human?, staff_name?, attachments?}, session_id}`.
                    // We honor `from_human` + per-message `staff_name` so
                    // historical-correct rendering survives a release.
                    if (data.type === 'new_message' && data.message) {
                        // Storm's envelope (back/api/chats.py:271,
                        // back/api/widget.py:453) does NOT carry a
                        // top-level `session_id` — the WS URL already
                        // pins the session. Accept any new_message on
                        // this socket and guard against a missing
                        // string `content` so a malformed frame can't
                        // crash the bubble render below.
                        if (typeof data.message.content !== 'string') {
                            console.warn('[handoff] staff WS new_message missing string content, skipping', data);
                            return;
                        }
                        setSelectedChat(prev => {
                            if (!prev || prev.session_id !== sid) return prev;
                            return {
                                ...prev,
                                messages: [...prev.messages, data.message],
                                // A frame with no `timestamp` must not blank out
                                // a good `last_message_at` (nor set it to undefined).
                                last_message_at: data.message.timestamp || prev.last_message_at || null,
                            };
                        });
                        // Keep the chat-list summary fresh (last-message
                        // preview / ordering / unread badge). Fire-and-
                        // forget; debounced naturally by the list endpoint.
                        fetchChats();
                        return;
                    }
                    // Future-proof: handoff status frames may also fan
                    // out on the same socket — merge what we recognize.
                    // Same session-scoping note as above (envelope has
                    // no session_id; the WS URL pins it).
                    if (data.type === 'handoff_status') {
                        setSelectedChat(prev => prev && prev.session_id === sid
                            ? { ...prev, human_handoff_status: data.status, human_handoff_by_name: data.by_name, human_handoff_by_user_id: data.by_user_id }
                            : prev);
                    }
                };
                ws.onerror = (err) => {
                    console.warn('[handoff] staff WS error', err);
                };
            } catch (err) {
                console.warn('[handoff] failed to open staff WS', err);
            }
        })();
        return () => {
            cancelled = true;
            try { ws?.close(); } catch { /* ignore */ }
            staffWsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChat?.session_id]);

    const fetchChats = async (from?: string, to?: string) => {
        setLoadingChats(true);
        try {
            const res = await chatsAPI.list(
                selectedAgentId || undefined,
                from ?? (dateFrom || undefined),
                to ?? (dateTo || undefined),
                'chat',
            );
            setChats(res.data);
            setSelectedChatIds(new Set());

            if (selectedChat) {
                const updated = res.data.find(c => c.session_id === selectedChat.session_id);
                if (updated) setSelectedChat(updated);
            }

            // Deep-link: auto-open a specific chat after load
            if (initialChatId) {
                const match = res.data.find(c => c.session_id === initialChatId);
                if (match) {
                    setSelectedChat(match);
                    setMobilePanel('chat');
                }
                onInitialChatHandled?.();
            }
        } catch (err) {
            showToast('Failed to load chats', 'error');
        } finally {
            setLoadingChats(false);
        }
    };

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedChatIds(new Set());
        } else {
            setSelectedChatIds(new Set(filteredChats.map(c => c.session_id)));
        }
    };

    const toggleSelectChat = (sessionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedChatIds(prev => {
            const next = new Set(prev);
            next.has(sessionId) ? next.delete(sessionId) : next.add(sessionId);
            return next;
        });
    };

    const handleReplyPaste = (e: React.ClipboardEvent) => {
        if (!e.clipboardData?.files?.length) return;
        const imageFiles = Array.from(e.clipboardData.files).filter(f => f.type.startsWith('image/'));
        if (!imageFiles.length) return;
        e.preventDefault();
        const newEntries = imageFiles.map(file => ({
            file,
            previewUrl: URL.createObjectURL(file),
            type: 'image' as const,
        }));
        setPendingReplyAttachments(prev => [...prev, ...newEntries]);
    };

    const handleReplyFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const newEntries = Array.from(e.target.files).map(file => ({
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
            type: (file.type.startsWith('image/') ? 'image' : 'document') as 'image' | 'document',
        }));
        setPendingReplyAttachments(prev => [...prev, ...newEntries]);
        e.target.value = '';
    };

    const removeReplyAttachment = (index: number) => {
        setPendingReplyAttachments(prev => {
            const entry = prev[index];
            if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleReply = async () => {
        // Plan-HumanAgentHandoff-2026-06-12 — belt-and-suspenders for
        // the input-gate (UI disables when !handoffByMe, but a focused
        // keyboard shortcut or assistive-tech bypass could still reach
        // here). Comments path is intentionally NOT gated.
        if (!handoffByMe) return;
        if (!selectedChat || (!replyText.trim() && pendingReplyAttachments.length === 0)) return;
        setSendingReply(true);
        try {
            let uploadedAttachments: Array<{ s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }> = [];
            if (pendingReplyAttachments.length > 0) {
                const results = await Promise.all(
                    pendingReplyAttachments.map(async pa => {
                        const formData = new FormData();
                        formData.append('agent_id', selectedChat.agent_id);
                        formData.append('session_id', selectedChat.session_id);
                        formData.append('file', pa.file);
                        try {
                            const res = await api.post('/api/playground/upload-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            return { data: res.data, error: null };
                        } catch {
                            return { data: null, error: 'Upload failed' };
                        }
                    })
                );
                if (results.some(r => r.error)) { showToast('Upload failed. Please upload files in the following formats only: Images, PDF, DOCX, or TXT.', 'error'); setSendingReply(false); return; }
                uploadedAttachments = results.map(r => r.data!);
            }
            setPendingReplyAttachments(prev => { prev.forEach(pa => { if (pa.previewUrl) URL.revokeObjectURL(pa.previewUrl); }); return []; });
            await chatsAPI.reply(selectedChat.session_id, replyText, uploadedAttachments.length > 0 ? uploadedAttachments : undefined);
            setReplyText('');
            const newMsg = {
                role: 'agent' as const,
                content: replyText,
                timestamp: new Date().toISOString(),
                attachments: uploadedAttachments.length > 0 ? uploadedAttachments as MessageAttachment[] : undefined,
            };
            setSelectedChat(prev => prev ? { ...prev, messages: [...prev.messages, newMsg] } : null);
            fetchChats();
        } catch (err) {
            showToast('Failed to send reply', 'error');
        } finally {
            setSendingReply(false);
        }
    };

    const handleAddComment = async () => {
        if (!selectedChat || !commentText.trim()) return;
        setAddingComment(true);
        try {
            const res = await chatsAPI.addComment(selectedChat.session_id, commentText);
            const newComment = res.data as ConversationComment;
            setCommentText('');
            setSelectedChat(prev => prev ? {
                ...prev,
                comments: [...(prev.comments ?? []), newComment],
            } : null);
            showToast('Comment added', 'success');
        } catch {
            showToast('Failed to add comment', 'error');
        } finally {
            setAddingComment(false);
        }
    };

    // Plan-HumanAgentHandoff-2026-06-09 §Staff UI — Take Over / Release
    // / Step In. Backend is first-takeover-wins (atomic mongo update),
    // a 409 means another staff already claimed it. Optimistic UI
    // update so the banner flips before the round trip lands; on error
    // we re-fetch via the existing `setSelectedChat` shape.
    const [handoffActionPending, setHandoffActionPending] = useState(false);
    const handoffStatus = (selectedChat?.human_handoff_status || 'none') as
        'none' | 'requested' | 'active' | 'released';
    const handoffByMe = !!(
        selectedChat?.human_handoff_by_user_id
        && currentUser?.id
        && selectedChat.human_handoff_by_user_id === currentUser.id
    );

    // `takeover` / `release-handoff` return a conversation doc that we spread
    // over the current one. A nullish creation timestamp in that payload would
    // clobber a good value and put "—" in the Details panel, so those three
    // keys only overwrite when the server actually sent something. Every other
    // key merges verbatim — `release-handoff` intentionally NULLs the handoff
    // fields (`human_handoff_by_name`, `..._by_user_id`), so a blanket
    // strip-nullish merge would break releasing.
    const mergeConversation = (prev: Conversation, patch: Partial<Conversation>): Conversation => {
        const merged = { ...prev, ...patch };
        merged.started_at = patch.started_at ?? prev.started_at;
        merged.created_at = patch.created_at ?? prev.created_at;
        merged.last_message_at = patch.last_message_at ?? prev.last_message_at;
        return merged;
    };

    const handleTakeOver = async () => {
        if (!selectedChat || handoffActionPending) return;
        setHandoffActionPending(true);
        try {
            const res = await chatsAPI.takeover(selectedChat.session_id);
            // Merge the backend's authoritative state — covers the
            // proactive Step In path where the status jumps straight to
            // active from none, AND the requested→active path.
            setSelectedChat(prev => prev ? mergeConversation(prev, res.data) : prev);
            showToast('You are now handling this conversation', 'success');
        } catch (err: any) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                showToast(detail || 'Already handled by another teammate', 'error');
                // Re-fetch the canonical state so our UI doesn't lie.
                try {
                    const refreshed = await chatsAPI.get(selectedChat.session_id);
                    setSelectedChat(refreshed.data);
                } catch { /* ignore */ }
            } else {
                console.error('takeover failed', err);
                showToast('Failed to take over conversation', 'error');
            }
        } finally {
            setHandoffActionPending(false);
        }
    };

    const handleReleaseHandoff = async () => {
        if (!selectedChat || handoffActionPending) return;
        setHandoffActionPending(true);
        try {
            const res = await chatsAPI.releaseHandoff(selectedChat.session_id);
            setSelectedChat(prev => prev ? mergeConversation(prev, res.data) : prev);
            showToast('Released back to AI', 'success');
        } catch (err) {
            console.error('release failed', err);
            showToast('Failed to release conversation', 'error');
        } finally {
            setHandoffActionPending(false);
        }
    };

    const handleAssign = async (userId: string) => {
        if (!selectedChat) return;
        setAssigning(true);
        try {
            await chatsAPI.assign(selectedChat.session_id, userId);
            showToast('Chat assigned successfully', 'success');
            const member = teamMembers.find(m => m.id === userId);
            setSelectedChat(prev => prev ? { ...prev, assigned_to: userId, assigned_to_name: member?.full_name } : null);
            fetchChats();
        } catch (err) {
            showToast('Failed to assign chat', 'error');
        } finally {
            setAssigning(false);
        }
    };

    const exportChats = (format: 'csv' | 'json', onlySelected = false) => {
        const source = onlySelected
            ? filteredChats.filter(c => selectedChatIds.has(c.session_id))
            : filteredChats;
        if (source.length === 0) return;

        let content = '';
        let mimeType = '';
        let ext = '';

        if (format === 'json') {
            content = JSON.stringify(source, null, 2);
            mimeType = 'application/json';
            ext = 'json';
        } else if (format === 'csv') {
            const esc = (v: any) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
            const headers = [
                'Session ID', 'Visitor ID', 'Visitor Name', 'Status',
                'Total Messages', 'Avg Response Time (ms)',
                'Created At', 'Last Message At',
                'Agent ID', 'Assigned To', 'Source'
            ];
            const rows = source.map(c => [
                c.session_id,
                c.visitor_id ?? '',
                c.visitor_name ?? '',
                c.status,
                c.total_messages ?? c.messages.length,
                c.avg_response_time_ms ?? '',
                c.created_at ?? c.started_at ?? '',
                c.last_message_at ?? '',
                c.agent_id,
                c.assigned_to_name ?? 'Unassigned',
                c.metadata?.source ?? '',
            ]);
            content = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
            mimeType = 'text/csv';
            ext = 'csv';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chats_export_${new Date().toISOString().split('T')[0]}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSelectChat = (chat: Conversation) => {
        setSelectedChat(chat);
        setMobilePanel('chat');
    };

    const handleBackToList = () => {
        setSelectedChat(null);
        setShowDetails(false);
        setMobilePanel('list');
    };

    const handleToggleDetails = () => {
        const next = !showDetails;
        setShowDetails(next);
        if (next) setMobilePanel('details');
        else setMobilePanel('chat');
    };

    const navigate = useNavigate();

    const handleNavigateToTicket = () => {
        if (!selectedChat?.ticket_id) return;
        navigate(`/tickets?ticket=${selectedChat.ticket_id}`);
    };

    return (
        <div className="flex-1 flex overflow-hidden md:p-4 lg:p-6 md:gap-0">
            <div className="flex-1 flex overflow-hidden md:rounded-2xl md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-800 bg-white dark:bg-gray-900">

                {/* ── Chat List Panel ── */}
                <div className={`
                    flex flex-col flex-shrink-0
                    border-r border-gray-200 dark:border-gray-800
                    bg-white dark:bg-gray-900
                    transition-all duration-300
                    ${mobilePanel === 'list' ? 'flex' : 'hidden'}
                    md:flex
                    ${selectedChat ? 'md:w-64 lg:w-72' : 'w-full md:w-full'}
                `}>
                    <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex-shrink-0 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <button
                                    onClick={toggleSelectAll}
                                    className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary transition-colors"
                                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                                >
                                    {allFilteredSelected
                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                        : someFilteredSelected
                                            ? <CheckSquare className="w-4 h-4 text-primary/50" />
                                            : <Square className="w-4 h-4" />}
                                </button>
                                <h2 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Conversations</h2>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => exportChats('csv')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Export all CSV">
                                    <Download className="w-4 h-4" />
                                </button>
                                <button onClick={() => exportChats('json')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium text-xs" title="Export all JSON">
                                    JSON
                                </button>
                            </div>
                        </div>

                        <select
                            className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            value={selectedAgentId}
                            onChange={(e) => {
                                setSelectedAgentId(e.target.value);
                                setSelectedChatIds(new Set());
                            }}
                        >
                            <option value="">All Agents</option>
                            {agents.map(a => (
                                <option key={a.id} value={a.id}>{a.config.agent_name}</option>
                            ))}
                        </select>

                        <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => { setDateFrom(e.target.value); setSelectedChatIds(new Set()); }}
                                className="flex-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0"
                                title="From date"
                            />
                            <span className="text-xs text-gray-400">–</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => { setDateTo(e.target.value); setSelectedChatIds(new Set()); }}
                                className="flex-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0"
                                title="To date"
                            />
                            {(dateFrom || dateTo) && (
                                <button
                                    onClick={() => { setDateFrom(''); setDateTo(''); setSelectedChatIds(new Set()); }}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                    title="Clear date filter"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {someFilteredSelected && (
                            <div className="flex items-center justify-between bg-primary/5 dark:bg-primary/10 rounded-lg px-2 py-1.5 border border-primary/20">
                                <span className="text-xs font-medium text-primary">
                                    {selectedChatIds.size} selected
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => exportChats('csv', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> CSV
                                    </button>
                                    <button
                                        onClick={() => exportChats('json', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> JSON
                                    </button>
                                    <button
                                        onClick={() => setSelectedChatIds(new Set())}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title="Clear selection"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {loadingChats ? (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                            </div>
                        ) : filteredChats.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                                {chats.length > 0 ? 'No chats match the current filters.' : 'No chats found.'}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {filteredChats.map(chat => {
                                    const isChecked = selectedChatIds.has(chat.session_id);
                                    return (
                                        <div
                                            key={chat.session_id}
                                            onClick={() => handleSelectChat(chat)}
                                            className={`group relative p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedChat?.session_id === chat.session_id ? 'bg-primary/5 dark:bg-primary/10 border-l-2 border-primary' : ''
                                                } ${isChecked ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <button
                                                    onClick={(e) => toggleSelectChat(chat.session_id, e)}
                                                    className={`flex-shrink-0 mt-0.5 transition-opacity ${isChecked || someFilteredSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                                        }`}
                                                    title={isChecked ? 'Deselect' : 'Select'}
                                                >
                                                    {isChecked
                                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                                        : <Square className="w-4 h-4 text-gray-400" />}
                                                </button>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate pr-2">
                                                            {chat.visitor_name || chat.session_id.substring(0, 12)}
                                                        </span>
                                                        <span className="text-xs text-gray-400 whitespace-nowrap">
                                                            {formatServerDate(chat.last_message_at ?? chat.created_at ?? chat.started_at)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${chat.status === 'active'
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                            }`}>
                                                            {chat.status}
                                                        </span>
                                                        {chat.assigned_to_name && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                                <UserIcon className="w-3 h-3" />
                                                                {chat.assigned_to_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Chat Messages Panel ── */}
                {selectedChat && (
                    <div className={`
                        flex-1 flex flex-col min-w-0
                        border-r border-gray-200 dark:border-gray-800
                        bg-white dark:bg-gray-900
                        ${mobilePanel === 'chat' ? 'flex' : 'hidden'}
                        md:flex
                        ${showDetails ? 'md:flex' : ''}
                    `}>
                        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50 shadow-sm z-10 flex-shrink-0">
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                                <button
                                    onClick={handleBackToList}
                                    className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
                                    title="Back to list"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0 text-sm">
                                    {(selectedChat.visitor_name || 'V').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base truncate">
                                        {selectedChat.visitor_name || selectedChat.session_id.substring(0, 12)}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${selectedChat.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                        {selectedChat.status}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                                {/* Plan-TalkToHumanToggleSchedule.md — display-only
                                    availability badge so staff know WHY a customer
                                    could (or couldn't) self-request a human. Purely
                                    informational: Take Over / Step In / Release are
                                    NEVER gated by this (§9-D). Rendered only once the
                                    backend includes the fields (avoids clutter on
                                    legacy payloads). */}
                                {(selectedChat.human_handoff_enabled !== undefined
                                    || selectedChat.human_handoff_available !== undefined) && (() => {
                                    const enabled = selectedChat.human_handoff_enabled !== false;
                                    const available = selectedChat.human_handoff_available !== false;
                                    const label = !enabled ? 'Handoff OFF' : !available ? 'Outside hours' : 'Available';
                                    const cls = !enabled
                                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                                        : !available
                                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                            : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
                                    const dot = !enabled ? 'bg-gray-400' : !available ? 'bg-amber-500' : 'bg-green-500';
                                    return (
                                        <span
                                            className={`hidden sm:inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg ${cls}`}
                                            title={
                                                selectedChat.human_handoff_hours_label
                                                    ? `Customer self-request availability: ${label} (${selectedChat.human_handoff_hours_label})`
                                                    : `Customer self-request availability: ${label}`
                                            }
                                        >
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`}></span>
                                            {label}
                                        </span>
                                    );
                                })()}
                                {hasAssignPermission && (
                                    <select
                                        className="hidden sm:block text-xs md:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 md:px-3 py-1 md:py-1.5 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 max-w-[120px] md:max-w-none"
                                        value={selectedChat.assigned_to || ""}
                                        onChange={(e) => handleAssign(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                )}
                                {/* Plan-HumanAgentHandoff-2026-06-09 §Staff UI
                                    — Take Over / Release / Step In control.
                                    Four states:
                                    - requested: primary "Take Over" CTA
                                    - active + by_me: secondary "Release to AI"
                                    - active + by_other: disabled badge
                                    - none: ghost "Step In" (proactive)
                                    Compact on mobile (375px): icon + truncated
                                    label, label hides at xs widths. */}
                                {handoffStatus === 'requested' && (
                                    <button
                                        type="button"
                                        onClick={handleTakeOver}
                                        disabled={handoffActionPending}
                                        className="inline-flex items-center gap-1 md:gap-1.5 text-xs md:text-sm font-medium bg-primary text-white px-2 md:px-3 py-1 md:py-1.5 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Take over this conversation from the AI"
                                    >
                                        <Hand className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                        <span className="hidden xs:inline sm:inline">Take Over</span>
                                    </button>
                                )}
                                {handoffStatus === 'active' && handoffByMe && (
                                    <button
                                        type="button"
                                        onClick={handleReleaseHandoff}
                                        disabled={handoffActionPending}
                                        className="inline-flex items-center gap-1 md:gap-1.5 text-xs md:text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 md:px-3 py-1 md:py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Hand the conversation back to the AI"
                                    >
                                        <Headphones className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                        <span className="hidden xs:inline sm:inline">Release to AI</span>
                                    </button>
                                )}
                                {handoffStatus === 'active' && !handoffByMe && (
                                    <span
                                        className="inline-flex items-center gap-1 md:gap-1.5 text-xs md:text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 md:px-3 py-1 md:py-1.5 rounded-lg cursor-not-allowed max-w-[140px] md:max-w-none truncate"
                                        title={`Handled by ${selectedChat?.human_handoff_by_name || 'teammate'}`}
                                    >
                                        <UserCheck className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                                        <span className="hidden xs:inline sm:inline truncate">
                                            {`Handled by ${selectedChat?.human_handoff_by_name || 'teammate'}`}
                                        </span>
                                    </span>
                                )}
                                {handoffStatus === 'none' && (
                                    <button
                                        type="button"
                                        onClick={handleTakeOver}
                                        disabled={handoffActionPending}
                                        className="inline-flex items-center gap-1 md:gap-1.5 text-xs md:text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 px-2 md:px-3 py-1 md:py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Step in and silence the AI for this conversation"
                                    >
                                        <Hand className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                        <span className="hidden xs:inline sm:inline">Step In</span>
                                    </button>
                                )}
                                <button
                                    onClick={handleToggleDetails}
                                    className={`p-1.5 md:p-2 rounded-lg transition-colors ${showDetails ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}
                                    title="Chat Details"
                                >
                                    <Info className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <button
                                    onClick={handleBackToList}
                                    className="hidden md:flex p-1.5 md:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                                    title="Close"
                                >
                                    <X className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Plan-HumanAgentHandoff-2026-06-09 §Staff UI —
                            banner under chat header. Three variants
                            mirror the chat-header button states:
                            - active + by_me: green confirmation
                            - active + by_other: neutral attribution
                            - requested: amber call-to-action */}
                        {handoffStatus === 'active' && handoffByMe && (
                            <div className="px-3 md:px-4 py-2 flex items-center gap-2 text-xs md:text-sm font-medium border-b border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100">
                                <UserCheck className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                                <span className="truncate">
                                    You're handling this conversation (AI replies paused)
                                </span>
                            </div>
                        )}
                        {handoffStatus === 'active' && !handoffByMe && (
                            <div className="px-3 md:px-4 py-2 flex items-center gap-2 text-xs md:text-sm font-medium border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200">
                                <UserCheck className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                                <span className="truncate">
                                    {`${selectedChat?.human_handoff_by_name || 'A teammate'} is handling this conversation`}
                                </span>
                            </div>
                        )}
                        {handoffStatus === 'requested' && (
                            <div className="px-3 md:px-4 py-2 flex items-center gap-2 text-xs md:text-sm font-medium border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100">
                                <Hand className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                                <span className="truncate">
                                    Customer requested human help — Take Over to claim
                                </span>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-4 bg-gray-50 dark:bg-[#0a0a0a]">
                            {selectedChat.messages.filter(msg => msg.role !== 'tool').map((msg, idx) => (
                                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] md:max-w-[80%] min-w-0 [overflow-wrap:anywhere] rounded-2xl px-3 md:px-4 py-2 md:py-2.5 shadow-sm ${msg.role === 'user'
                                        ? 'bg-primary text-white rounded-br-none'
                                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-none'
                                        }`}>
                                        <AgentMessageContent content={msg.content} role={msg.role === 'user' ? 'user' : 'agent'} isDark={isDark} />
                                        {msg.attachments && msg.attachments.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {msg.attachments.map((att, i) =>
                                                    att.type === 'image' ? (
                                                        <img
                                                            key={i}
                                                            src={att.s3_url}
                                                            alt={att.filename}
                                                            className="rounded-lg mt-1 cursor-pointer"
                                                            style={{ maxWidth: '200px' }}
                                                            onClick={() => window.open(att.s3_url, '_blank')}
                                                        />
                                                    ) : (
                                                        <a
                                                            key={i}
                                                            href={att.s3_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-sm underline mt-1"
                                                        >
                                                            📄 {att.filename}
                                                        </a>
                                                    )
                                                )}
                                            </div>
                                        )}
                                        <span className={`text-[10px] mt-1 block ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-400 dark:text-gray-500'}`}>
                                            {formatServerTime(msg.timestamp, [], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="p-3 md:p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0 flex flex-col gap-3">
                            {pendingReplyAttachments.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {pendingReplyAttachments.map((pa, idx) => (
                                        <div key={idx} className="relative flex-shrink-0">
                                            {pa.type === 'image' ? (
                                                <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-300">
                                                    <img src={pa.previewUrl} alt="attachment" className="w-full h-full object-cover" />
                                                </div>
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg border-2 border-gray-300 flex items-center justify-center text-xs text-gray-500">
                                                    📄
                                                </div>
                                            )}
                                            <button onClick={() => removeReplyAttachment(idx)} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-700 text-white flex items-center justify-center text-xs">×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Plan-HumanAgentHandoff-2026-06-12 — reply
                                input is gated on `handoffByMe`. The AI
                                handles replies until staff takes over;
                                another teammate handling means we lock
                                this surface for them. Internal Comments
                                below stay unrestricted on purpose —
                                they're notes, not customer-facing. */}
                            {(() => {
                                const canReply = handoffByMe;
                                const replyPlaceholder = canReply
                                    ? 'Type a direct reply...'
                                    : handoffStatus === 'active' && !handoffByMe
                                        ? `${selectedChat?.human_handoff_by_name || 'A teammate'} is handling this chat`
                                        : handoffStatus === 'released'
                                            ? 'AI is handling this chat — Step In above to reply'
                                            : 'Take Over above to reply';
                                return (
                            <div className={`flex items-end gap-2 bg-gray-50 dark:bg-gray-800 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all ${!canReply ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={replyPlaceholder}
                                    disabled={!canReply}
                                    className="flex-1 max-h-32 min-h-[40px] bg-transparent border-none focus:ring-0 resize-none py-2.5 px-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:cursor-not-allowed"
                                    onKeyDown={(e) => {
                                        if (!canReply) return;
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleReply();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        if (!canReply) return;
                                        handleReplyPaste(e);
                                    }}
                                />
                                <button
                                    onClick={() => { if (!canReply) return; replyFileInputRef.current?.click(); }}
                                    disabled={!canReply}
                                    className="p-2.5 md:p-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors mb-0.5 disabled:cursor-not-allowed"
                                    title={canReply ? 'Attach file' : replyPlaceholder}
                                >
                                    <Paperclip className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <input ref={replyFileInputRef} type="file" accept="image/*,.pdf,.docx,.txt" multiple className="hidden" onChange={handleReplyFileSelect} />
                                <button
                                    onClick={handleReply}
                                    disabled={!canReply || (!replyText.trim() && pendingReplyAttachments.length === 0) || sendingReply}
                                    className="p-2.5 md:p-3 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-0.5 mr-0.5"
                                >
                                    {sendingReply ? <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-4 h-4 md:w-5 md:h-5" />}
                                </button>
                            </div>
                                );
                            })()}
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex flex-col gap-0.5">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Chat here will be directly replied to the customer.
                                    </p>
                                    {/* Plan-HumanAgentHandoff-2026-06-09 §Staff
                                        UI — direct staff to per-conversation
                                        Take Over instead of the workspace-wide
                                        Publish kill-switch below. */}
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                        Tip: to silence the AI for just this conversation, use Take Over above.
                                    </p>
                                </div>
                                {currentAgent && (
                                    <label className={`flex items-center gap-2 cursor-pointer w-max ${updatingPublished ? 'opacity-50 pointer-events-none' : ''}`}>
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                            Agent's Publish Status: {publishedEnabled ? <span className="text-blue-600 dark:text-blue-400">Live</span> : <span className="text-gray-500">Draft</span>}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleTogglePublished}
                                            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${publishedEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                                        >
                                            <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${publishedEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                        </button>
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Details Panel ── */}
                {selectedChat && showDetails && (
                    <div className={`
                        flex-shrink-0 overflow-hidden
                        bg-gray-50 dark:bg-gray-900
                        border-l border-gray-200 dark:border-gray-800
                        ${mobilePanel === 'details' ? 'flex flex-col w-full' : 'hidden'}
                        lg:flex lg:flex-col lg:w-72
                        md:flex md:flex-col md:w-64
                        p-4 md:p-5
                    `}>
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setMobilePanel('chat')}
                                    className="md:hidden p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
                                    title="Back to chat"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <h3 className="font-semibold text-gray-900 dark:text-white">Details</h3>
                            </div>
                            <button onClick={() => { setShowDetails(false); setMobilePanel('chat'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-5">
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Session Info</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{selectedChat.status}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Started</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{formatServerDateTime(selectedChat.started_at ?? selectedChat.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Messages</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedChat.messages.length}</p>
                                    </div>
                                </div>
                            </div>

                            {/* CSAT Rating */}
                            {selectedChat.csat_score != null && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Customer Satisfaction</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-2">
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <span key={star} className={`text-lg ${star <= selectedChat.csat_score! ? 'text-amber-400' : 'text-gray-200 dark:text-gray-600'}`}>
                                                    ★
                                                </span>
                                            ))}
                                            <span className="ml-1 text-sm font-semibold text-gray-900 dark:text-white">{selectedChat.csat_score}/5</span>
                                        </div>
                                        {selectedChat.csat_feedback && (
                                            <p className="text-xs text-gray-600 dark:text-gray-400 italic">"{selectedChat.csat_feedback}"</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Assignment</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                    {selectedChat.assigned_to_name ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                                                {selectedChat.assigned_to_name.charAt(0)}
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedChat.assigned_to_name}</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Unassigned</p>
                                    )}
                                </div>
                            </div>

                            {selectedChat.ticket_id && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Linked Ticket</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <button
                                            onClick={handleNavigateToTicket}
                                            className="flex items-center space-x-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            <span>View Ticket #{selectedChat.ticket_id.split('-')[1] || selectedChat.ticket_id}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {hasAssignPermission && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reassign</p>
                                    <select
                                        className="sm:hidden w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                                        value={selectedChat.assigned_to || ""}
                                        onChange={(e) => handleAssign(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">System Data</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Session ID</p>
                                        <p className="text-xs font-mono text-gray-900 dark:text-white break-all">{selectedChat.session_id}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Agent ID</p>
                                        <p className="text-xs font-mono text-gray-900 dark:text-white break-all">{selectedChat.agent_id}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Internal Comments */}
                            <div className="border-t border-gray-200 dark:border-gray-800 pt-5 mt-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex-shrink-0">Internal Comments</p>

                                <div className="space-y-3 mb-4 pr-1">
                                    {(!selectedChat.comments || selectedChat.comments.length === 0) ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No comments yet.</p>
                                    ) : (
                                        selectedChat.comments.map((c: ConversationComment) => (
                                            <div key={c.comment_id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="font-medium text-xs text-gray-900 dark:text-white">{c.author_name}</span>
                                                    <span className="text-[10px] text-gray-400">{formatServerDateTime(c.created_at, [], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{c.text}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="flex-shrink-0 relative">
                                    <textarea
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        placeholder="Add an internal note..."
                                        className="w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 pb-10 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none h-24 shadow-sm"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleAddComment();
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleAddComment}
                                        disabled={!commentText.trim() || addingComment}
                                        className="absolute bottom-2 right-2 p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                        title="Add comment"
                                    >
                                        {addingComment ? <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ChatsTab;
